import { FC, PropsWithChildren, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { Button, Drawer } from 'react-daisyui';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { DefaultLayoutProvider, useDefaultLayoutContext } from '@/layouts/Default/DefaultLayoutProvider.tsx';
import Portal from '@/components/shared/Portal/Portal.tsx';
import { cn } from '@/utils/classNames.ts';
import Navbar from '@/components/ui/Navbar/Navbar';
import BottomNav from '@/components/ui/BottomNav/BottomNav';
import TasksIcon from '@/components/ui/icons/TasksIcon';
import ProgressIcon from '@/components/ui/icons/ProgressIcon';
import RocketIcon from '@/components/ui/icons/RocketIcon';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import { useAuthContext } from '@/context/AuthContext';

type LayoutProps = PropsWithChildren<{
  mainClassName?: string;
  showLogo?: boolean;
  showSearch?: boolean;
}>;

const Layout: FC<LayoutProps> = (props) => {
  return (
    <DefaultLayoutProvider>
      <LayoutInner {...props} />
    </DefaultLayoutProvider>
  );
};

const LayoutInner: FC<LayoutProps> = ({ mainClassName, showLogo = true, showSearch = true }) => {
  const { containerElementRef, searchQuery, setSearchQuery } = useDefaultLayoutContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuthContext();

  const isLessonDetails = location.pathname.startsWith('/details/');
  const isDashboard = location.pathname === Routes.DASHBOARD;

  const navItems = [
    {
      icon: <TasksIcon className="w-5 h-5" />,
      onClick: () => navigate(Routes.DASHBOARD),
      active: location.pathname === Routes.DASHBOARD,
    },
    {
      icon: <ProgressIcon className="w-6 h-6" />,
      onClick: () => navigate(Routes.DASHBOARD),
    },
    {
      icon: <RocketIcon className="w-6 h-6" />,
      onClick: () => navigate(Routes.DASHBOARD),
    },
    {
      icon: <LogoutIcon sx={{ width: '24px', height: '24px' }} />,
      onClick: logout,
    },
  ];

  return (
    <>
      <div className="flex grow min-h-screen flex-col lg:flex-row w-screen h-screen bg-bg-light">
        <Navbar
          className={cn((isLessonDetails || (!showLogo && !showSearch)) && "hidden")}
          logoLink={Routes.DASHBOARD}
          showLogo={showLogo}
          showSearch={showSearch && isDashboard}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          variant="user"
          navItems={navItems}
        />
        <main
          ref={containerElementRef}
          id="default-layout-content-container"
          className={cn(
            'z-0 bg-bg-light relative flex flex-col grow gap-4 md:gap-8 lg:max-h-screen lg:overflow-auto p-4 pb-24 md:p-10 md:pb-24 lg:p-12 lg:pb-12',
            (isLessonDetails || (!showLogo && !showSearch)) ? 'lg:max-w-full' : 'lg:max-w-[calc(100%-122px)]',
            mainClassName
          )}
        >
          {showSearch && isDashboard && (
            <div className="hidden lg:flex justify-end relative z-10">
              <div className="relative w-[186px]">
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 !w-5 !h-5" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-[40px] pl-12 pr-4 rounded-full bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4E7DF6]/30"
                />
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
      <BottomNav 
        items={navItems}
        variant="user"
      />
      <LayoutDrawer />
    </>
  );
};

const InnerDrawer: FC<PropsWithChildren> = ({ children }) => {
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={'layout-drawer'}>
      {children}
    </Portal>
  );
};

const LayoutDrawer: FC = () => {
  const { isDrawerOpen, setIsDrawerOpen } = useDefaultLayoutContext();
  const { t } = useTranslation();

  return (
    <Drawer
      className="z-10"
      end={true}
      side={
        <div className="flex flex-col max-w-[100vw] max-h-[100dvh] h-[100dvh] bg-base-100 overflow-auto">
          <div className="bg-base-100 pt-6 pb-4 px-6 flex justify-end sticky top-0 z-10">
            <Button
              color="ghost"
              size="sm"
              endIcon={<XMarkIcon className="w-6 h-6" />}
              onClick={() => setIsDrawerOpen(false)}
            >
              {t('button.close')}
            </Button>
          </div>
          <div id="layout-drawer" className="empty:hidden pb-6 px-6" />
        </div>
      }
      open={isDrawerOpen}
      onClickOverlay={() => (isDrawerOpen ? setIsDrawerOpen(false) : null)}
    ></Drawer>
  );
};

export default {
  Layout,
  Drawer: InnerDrawer,
};
